import { useState, type ReactNode } from "react";
import {
  FiAlertOctagon,
  FiAlertTriangle,
  FiBookmark,
  FiCheckCircle,
  FiChevronRight,
  FiImage,
  FiInfo,
  FiLink,
  FiMaximize2,
} from "react-icons/fi";

import type {
  UserGuideBlock,
  UserGuideInline,
  UserGuideTableCell,
} from "../../entities/user-guide";
import { classifyLink, stackableHeaders } from "./guideSupport";
import { useGuide } from "./guideContext";

type CalloutTone = "info" | "note" | "success" | "warning" | "error";

const calloutContent: Record<CalloutTone, { label: string; icon: ReactNode }> =
  {
    info: { label: "Информация", icon: <FiInfo aria-hidden="true" /> },
    note: { label: "Заметка", icon: <FiBookmark aria-hidden="true" /> },
    success: { label: "Готово", icon: <FiCheckCircle aria-hidden="true" /> },
    warning: {
      label: "Внимание",
      icon: <FiAlertTriangle aria-hidden="true" />,
    },
    error: { label: "Важно", icon: <FiAlertOctagon aria-hidden="true" /> },
  };

/** Дерево блоков инструкции. Из данных строятся обычные элементы, чужой HTML сюда не попадает. */
export function GuideBlocks({
  blocks,
  lead = false,
}: {
  blocks: readonly UserGuideBlock[];
  /** Первый абзац страницы крупнее остальных: это вступление. */
  lead?: boolean;
}) {
  return (
    <>
      {blocks.map((block, index) => (
        <GuideBlock key={index} block={block} lead={lead && index === 0} />
      ))}
    </>
  );
}

function GuideBlock({ block, lead }: { block: UserGuideBlock; lead: boolean }) {
  switch (block.type) {
    case "heading":
      return <Heading block={block} />;
    case "paragraph":
      return (
        <p className={lead ? "guide-lead" : undefined}>
          <Inlines inlines={block.inlines} />
        </p>
      );
    case "list": {
      const List = block.ordered ? "ol" : "ul";
      return (
        <List
          className="guide-list"
          start={block.ordered && block.start !== 1 ? block.start : undefined}
        >
          {block.items.map((item, index) => (
            <li key={index}>
              <GuideBlocks blocks={item} />
            </li>
          ))}
        </List>
      );
    }
    case "quote":
      return (
        <blockquote className="guide-quote">
          <GuideBlocks blocks={block.blocks} />
        </blockquote>
      );
    case "callout": {
      const { label, icon } = calloutContent[block.tone];
      return (
        <aside
          className={`guide-callout guide-callout--${block.tone}`}
          role="note"
        >
          <span className="guide-callout__icon">{icon}</span>
          <div className="guide-callout__body">
            <span className="visually-hidden">{label}. </span>
            <GuideBlocks blocks={block.blocks} />
          </div>
        </aside>
      );
    }
    case "expand":
      return (
        <details className="guide-expand">
          <summary>
            <FiChevronRight aria-hidden="true" />
            <span>{block.title === "" ? "Подробнее" : block.title}</span>
          </summary>
          <div className="guide-expand__body">
            <GuideBlocks blocks={block.blocks} />
          </div>
        </details>
      );
    case "code":
      return (
        <pre className="guide-code" tabIndex={0}>
          <code>{block.text}</code>
        </pre>
      );
    case "table":
      return <Table rows={block.rows} />;
    case "image":
      return <Figure block={block} />;
    case "rule":
      return <hr className="guide-rule" />;
  }
}

function Heading({
  block,
}: {
  block: Extract<UserGuideBlock, { type: "heading" }>;
}) {
  const { goToSection } = useGuide();
  const Tag = block.level === 2 ? "h2" : block.level === 3 ? "h3" : "h4";

  return (
    <Tag id={block.id} className="guide-heading">
      <span>{block.text}</span>
      <a
        className="guide-heading__anchor"
        href={`#${block.id}`}
        aria-label={`Ссылка на раздел «${block.text}»`}
        onClick={(event) => {
          event.preventDefault();
          goToSection(block.id);
        }}
      >
        <FiLink aria-hidden="true" />
      </a>
    </Tag>
  );
}

function Inlines({ inlines }: { inlines: readonly UserGuideInline[] }) {
  return (
    <>
      {inlines.map((inline, index) => (
        <Inline key={index} inline={inline} />
      ))}
    </>
  );
}

function Inline({ inline }: { inline: UserGuideInline }) {
  const { goToSection } = useGuide();

  if (inline.type === "break") return <br />;
  if (inline.type === "status") {
    return (
      <span className={`guide-status guide-status--${inline.tone}`}>
        {inline.text}
      </span>
    );
  }

  let content: ReactNode = inline.text;
  for (const mark of inline.marks) {
    content = wrapMark(mark, content);
  }

  const link = classifyLink(inline.href);
  if (link === null) return <>{content}</>;
  if (link.kind === "anchor") {
    return (
      <a
        href={`#${link.id}`}
        onClick={(event) => {
          event.preventDefault();
          goToSection(link.id);
        }}
      >
        {content}
      </a>
    );
  }
  if (link.kind === "contact") return <a href={link.href}>{content}</a>;

  return (
    <a href={link.href} target="_blank" rel="noreferrer noopener">
      {content}
      <span className="visually-hidden"> (откроется в новой вкладке)</span>
    </a>
  );
}

function wrapMark(
  mark: "strong" | "em" | "code" | "strike" | "underline" | "sub" | "sup",
  content: ReactNode,
): ReactNode {
  switch (mark) {
    case "strong":
      return <strong>{content}</strong>;
    case "em":
      return <em>{content}</em>;
    case "code":
      return <code>{content}</code>;
    case "strike":
      return <s>{content}</s>;
    case "underline":
      return <u>{content}</u>;
    case "sub":
      return <sub>{content}</sub>;
    case "sup":
      return <sup>{content}</sup>;
  }
}

function Table({ rows }: { rows: readonly (readonly UserGuideTableCell[])[] }) {
  const labels = stackableHeaders(rows);
  const [head, ...rest] = rows;
  const hasHead = head?.every((cell) => cell.header) ?? false;
  const bodyRows = hasHead ? rest : rows;

  return (
    <div
      className="guide-table-wrap"
      role="region"
      aria-label="Таблица"
      tabIndex={0}
    >
      <table
        className={`guide-table${labels === null ? "" : " guide-table--stackable"}`}
      >
        {hasHead && head !== undefined ? (
          <thead>
            <tr>
              {head.map((cell, index) => (
                <th key={index} scope="col" {...spans(cell)}>
                  <GuideBlocks blocks={cell.blocks} />
                </th>
              ))}
            </tr>
          </thead>
        ) : null}
        <tbody>
          {bodyRows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((cell, index) =>
                cell.header ? (
                  <th key={index} scope="row" {...spans(cell)}>
                    <GuideBlocks blocks={cell.blocks} />
                  </th>
                ) : (
                  <td key={index} data-label={labels?.[index]} {...spans(cell)}>
                    <GuideBlocks blocks={cell.blocks} />
                  </td>
                ),
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function spans(cell: UserGuideTableCell) {
  return {
    colSpan: cell.colspan > 1 ? cell.colspan : undefined,
    rowSpan: cell.rowspan > 1 ? cell.rowspan : undefined,
  };
}

function Figure({
  block,
}: {
  block: Extract<UserGuideBlock, { type: "image" }>;
}) {
  const { images, openImage, imageFailed } = useGuide();
  // Запоминаем именно адрес, что не загрузился: когда страница получит свежую
  // подписанную ссылку, картинка сама попробует ещё раз.
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const image = block.image_id === null ? undefined : images[block.image_id];
  const failed = image !== undefined && failedUrl === image.url;
  // Подпись показывается всегда, а описание идёт в alt только тогда, когда
  // подпись другая: иначе экранный диктор прочитает одно и то же дважды.
  const caption = block.caption ?? (block.alt === "" ? null : block.alt);
  const alt = block.caption === null ? "" : block.alt;

  if (image === undefined || failed || block.image_id === null) {
    return (
      <figure className="guide-figure guide-figure--missing">
        <div className="guide-figure__missing">
          <FiImage aria-hidden="true" />
          <span>
            {failed
              ? "Изображение не загрузилось. Обновите страницу."
              : "Изображение недоступно"}
          </span>
        </div>
        {caption === null ? null : <figcaption>{caption}</figcaption>}
      </figure>
    );
  }
  const imageId = block.image_id;
  const width = image.width ?? block.width;
  const height = image.height ?? block.height;

  return (
    <figure className="guide-figure">
      <button
        className="guide-figure__frame"
        type="button"
        onClick={() => openImage(imageId)}
        aria-label={`Открыть крупно${caption === null ? "" : `: ${caption}`}`}
      >
        <img
          src={image.url}
          alt={alt}
          width={width ?? undefined}
          height={height ?? undefined}
          loading="lazy"
          decoding="async"
          onError={() => {
            setFailedUrl(image.url);
            imageFailed();
          }}
        />
        <span className="guide-figure__zoom" aria-hidden="true">
          <FiMaximize2 />
        </span>
      </button>
      {caption === null ? null : <figcaption>{caption}</figcaption>}
    </figure>
  );
}
